import { createMuiTheme } from "@material-ui/core";

import { IColors } from "../models/Colors";
import { BentoTheme, ThemeName, WeVideoTheme } from "../models/WeVideoTheme";
import {
	ColorTokensLightTheme as bentoLight,
	ElevationLightTheme as elevationLight,
} from "../tokens/colors/ColorTokens";
import { applyOpacity } from "../utils/colors";
import { DEFAULT_BORDER_RADIUS, DEFAULT_SPACING, LIGHT_NEUTRALS, TYPERAMP } from "../utils/const";
import { getBentoTheme, getWeVideoTheme } from "../utils/parsers";

const NEUTRALS = LIGHT_NEUTRALS;

const colors: IColors = {
	layer: {
		1: {
			rest: NEUTRALS.N000,
			hover: NEUTRALS.N200,
		},
	},
	button: {
		primary: {
			text: {
				rest: NEUTRALS.N000,
				hover: NEUTRALS.N000,
				disabled: NEUTRALS.N065,
			},
			container: {
				rest: NEUTRALS.BRAND.PRIMARY,
				hover: NEUTRALS.BRAND.SHADE200,
				disabled: NEUTRALS.BRAND.TINT100,
			},
		},
		secondary: {
			text: {
				rest: NEUTRALS.N700,
				hover: NEUTRALS.N700,
				disabled: NEUTRALS.N400,
			},
			container: {
				rest: NEUTRALS.N200,
				hover: NEUTRALS.N300,
				disabled: NEUTRALS.N200,
			},
		},
		tertiary: {
			text: {
				rest: NEUTRALS.N000,
				hover: NEUTRALS.N000,
				disabled: NEUTRALS.N000,
			},
			container: {
				rest: NEUTRALS.GREEN.PRIMARY,
				hover: NEUTRALS.GREEN.SHADE100,
				disabled: NEUTRALS.GREEN.TINT50,
			},
		},
		textButton: {
			primary: {
				rest: NEUTRALS.BRAND.PRIMARY,
				disabled: NEUTRALS.BRAND.TINT100,
			},
			tertiary: {
				rest: NEUTRALS.GREEN.PRIMARY,
				disabled: NEUTRALS.GREEN.TINT50,
			},
		},
		// TODO Fix and move radio in form: {...}
		radio: {
			outline: {
				selected: NEUTRALS.BRAND.PRIMARY,
				disabled: NEUTRALS.N400,
				notSelected: NEUTRALS.N100,
			},
			fill: {
				selected: NEUTRALS.BRAND.PRIMARY,
				disabled: NEUTRALS.N400,
				notSelected: NEUTRALS.N600,
			},
			hover: {
				selected: NEUTRALS.BRAND.SHADE200,
				disabled: NEUTRALS.N300,
				notSelected: NEUTRALS.N300,
			},
		},
	},

	form: {
		checkbox: {
			outline: {
				fill: NEUTRALS.N200,
				stroke: NEUTRALS.N600,
			},
			outlineDisabled: {
				fill: NEUTRALS.N300,
				stroke: NEUTRALS.N400,
			},
			hover: {
				fill: NEUTRALS.BRAND.SHADE200,
				stroke: NEUTRALS.N300,
			},
		},
	},
	controls: {
		scrollbar: NEUTRALS.N300,
	},
	divider: NEUTRALS.N300,
	elevation: {
		one: "0px 2px 3px rgba(0, 0, 0, 0.18)",
		two: "0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.12)",
		three:
			"0px 8px 17px rgba(0, 0, 0, 0.1), 0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.06)",
	},
	error: NEUTRALS.RED.PRIMARY,
	outline: NEUTRALS.BRAND.SHADE200,
	icon: {
		primary: NEUTRALS.N600,
		disabled: NEUTRALS.N400,
		error: NEUTRALS.RED.PRIMARY,
	},
	input: {
		box: {
			border: NEUTRALS.N300,
			active: NEUTRALS.BRAND.PRIMARY,
			disabled: NEUTRALS.N200,
			error: NEUTRALS.RED.PRIMARY,
			fill: NEUTRALS.N100,
		},
	},
	knob: NEUTRALS.N000,
	presence: {
		zero: "#037CB0",
		one: "#BB5202",
		two: "#887615",
		three: "#BB2E02",
		four: "#12865C",
		five: "#007C84",
		six: "#0C8043",
		seven: "#B24545",
		eight: "#8B02BB",
		nine: "#545454",
		ten: "#8389CC",
		eleven: "#38AAAF",
	},
	states: {
		hover: NEUTRALS.N200,
		selected: NEUTRALS.N700,
	},
	rows: {
		rest: NEUTRALS.N000,
		hover: NEUTRALS.N200,
		selected: applyOpacity(`${NEUTRALS.BRAND.PRIMARY}`, 15),
		hoverSelected: applyOpacity(`${NEUTRALS.BRAND.PRIMARY}`, 27),
	},
	slider: {
		value: NEUTRALS.BRAND.PRIMARY,
		max: NEUTRALS.N300,
		marker: NEUTRALS.N400,
		rail: NEUTRALS.N400,
	},
	stepper: {
		button: NEUTRALS.N300,
	},
	surface: {
		mainBg: NEUTRALS.N000,
		secondaryBg: NEUTRALS.N200,
		tertiaryBg: NEUTRALS.YELLOW.TINT200,
		one: NEUTRALS.N000,
		two: NEUTRALS.N000,
		three: NEUTRALS.N000,
		four: NEUTRALS.N1006,
		input: NEUTRALS.N100,
		overlay: applyOpacity(NEUTRALS.N700, 50),
	},
	surfaces: NEUTRALS.N000,
	graphics: {
		fill: NEUTRALS.N300,
	},
	tab: {
		text: {
			selected: NEUTRALS.N700,
			hover: NEUTRALS.N600,
			notSelected: NEUTRALS.N600,
		},
		underline: {
			selected: NEUTRALS.BRAND.PRIMARY,
			hover: NEUTRALS.N300,
		},
	},
	pill: {
		text: NEUTRALS.N600,
		textSelected: NEUTRALS.N700,
		background: NEUTRALS.N000,
		backgroundSelected: NEUTRALS.N200,
		backgroundHover: NEUTRALS.N300,
		border: NEUTRALS.N300,
		gradient: NEUTRALS.N000,
		icon: NEUTRALS.N600,
		disabled: NEUTRALS.N200,
	},

	logo: {
		primary: NEUTRALS.BRAND.PRIMARY,
		secondary: NEUTRALS.GRAY.TINT600,
		tertiary: NEUTRALS.GRAY.TINT600,
	},
	weVideoLogo: {
		primary: NEUTRALS.BRAND.SECONDARY,
		secondary: NEUTRALS.N000,
		tertiary: NEUTRALS.N700,
		grayScale: {
			primary: NEUTRALS.N700,
			secondary: NEUTRALS.N000,
		},
	},

	text: {
		primary: NEUTRALS.N700,
		secondary: NEUTRALS.N600,
		tertiary: NEUTRALS.N500,
		disabled: NEUTRALS.N400,
		misc: NEUTRALS.N1100,
		description: NEUTRALS.N500,
		selected: NEUTRALS.N000,
		error: NEUTRALS.RED.PRIMARY,
	},
	toggle: {
		selected: {
			border: NEUTRALS.N500,
			fill: NEUTRALS.BRAND.PRIMARY,
		},
		notSelected: {
			border: NEUTRALS.N500,
			fill: NEUTRALS.N300,
		},
	},
	trimmer: {
		transitionBorder: NEUTRALS.BRAND.TINT200,
		transitionFill: NEUTRALS.BRAND.TINT250,
		trimmedZoneFill: applyOpacity(`${NEUTRALS.N700}`, 50),
		trimZone: NEUTRALS.N500,
		trimmerHandleLine: NEUTRALS.N000,
	},
	transitionHint: {
		outerBackground: NEUTRALS.N000,
		background: NEUTRALS.N600,
		outline: NEUTRALS.N600,
		cross: NEUTRALS.N000,
	},
	essentials: {
		red: {
			primary: NEUTRALS.RED.PRIMARY,
			tint100: NEUTRALS.RED.TINT100,
		},
		brand: {
			primary: NEUTRALS.BRAND.PRIMARY,
			tint100: NEUTRALS.BRAND.TINT100,
			tint200: NEUTRALS.BRAND.TINT200,
			shade100: NEUTRALS.BRAND.SHADE100,
			shade200: NEUTRALS.BRAND.SHADE200,
			shade250: NEUTRALS.BRAND.SHADE250,
		},
		gray: {
			tint50: NEUTRALS.GRAY.TINT50,
			tint250: NEUTRALS.GRAY.TINT250,
			tint300: NEUTRALS.GRAY.TINT300,
			tint400: NEUTRALS.GRAY.TINT400,
			tint500: NEUTRALS.GRAY.TINT500,
			tint550: NEUTRALS.GRAY.TINT550,
			tint625: NEUTRALS.GRAY.TINT625,
			dark: NEUTRALS.GRAY.DARK,
			tint04: NEUTRALS.N004,
			chevronGrey: "#cccccc",
		},
		green: {
			primary: NEUTRALS.GREEN.PRIMARY,
			tint50: NEUTRALS.GREEN.TINT50,
			tint100: NEUTRALS.GREEN.TINT100,
			shade100: NEUTRALS.GREEN.SHADE100,
		},
		yellow: {
			primary: NEUTRALS.YELLOW.PRIMARY,
			tint100: NEUTRALS.YELLOW.TINT100,
			tint200: NEUTRALS.YELLOW.TINT200,
		},
		white: {
			primary: NEUTRALS.N000,
			tint50: NEUTRALS.N050,
		},
		black: {
			primary: NEUTRALS.N1000,
		},
		purple: {
			primary: NEUTRALS.PURPLE.PRIMARY,
		},
	},
	progress: { bar: NEUTRALS.BRAND.PRIMARY, track: NEUTRALS.N300 },
	loader: NEUTRALS.N2000,
	dashboardCards: {
		classroom: NEUTRALS.TEMPORARY.CLASSROOM_BLUE,
		classic: NEUTRALS.TEMPORARY.CLASSIC_GRAY,
		admin: NEUTRALS.TEMPORARY.ADMIN_YELLOW,
		superAdmin: NEUTRALS.TEMPORARY.ADMIN_YELLOW,
		playPosit: NEUTRALS.TEMPORARY.PLAYPOSIT_BLUE,
	},
	betaTag: NEUTRALS.PURPLE.PRIMARY,
	appSwitcher: {
		icon: NEUTRALS.N600,
	},
	waveform: {
		backgroundGradientStart: NEUTRALS.GRAY.TINT250,
		backgroundGradientEnd: NEUTRALS.GRAY.TINT250,
		stroke: NEUTRALS.N500,
		fill: NEUTRALS.N500,
	},
	studentView: {
		color: NEUTRALS.N700,
	},
	space: {
		nextgen: NEUTRALS.BRAND.PRIMARY,
		classic: NEUTRALS.GRAY.TINT625,
		interactivity: "#8B02BB",
		admin: "#887615",
	},
	toaster: {
		info: NEUTRALS.N600,
		success: NEUTRALS.GREEN.TINT100,
		error: NEUTRALS.RED.PRIMARY,
	},
	link: {
		primary: NEUTRALS.BRAND.PRIMARY,
		hover: NEUTRALS.BRAND.PRIMARY,
		visited: NEUTRALS.PURPLE.TINT100,
	},
	tooltip: {
		background: NEUTRALS.N000,
	},
};

const weVideoTheme: WeVideoTheme = getWeVideoTheme(colors);
const bentoTheme: BentoTheme = getBentoTheme(bentoLight, elevationLight);

export const LightTheme = createMuiTheme({
	name: ThemeName.LIGHT,
	spacing: DEFAULT_SPACING,
	shape: { borderRadius: DEFAULT_BORDER_RADIUS },
	palette: {
		type: "light",
		// Brand
		primary: {
			main: colors.button.primary.container?.rest!,
			light: colors.button.primary.container?.hover,
			contrastText: colors.button.primary.text.rest,
		},
		secondary: {
			main: colors.button.secondary.container?.rest!,
			light: colors.button.secondary.container?.hover,
			contrastText: colors.button.secondary.text.rest,
		},
		background: {
			default: colors.surface.mainBg,
		},
		text: {
			primary: colors.text.primary,
			secondary: colors.text.secondary,
			disabled: colors.text.disabled,
		},

		divider: colors.divider,
		error: { main: colors.error },
	},
	typography: {
		fontFamily: "Lato, lato",
		h1: { ...TYPERAMP.B1_TITLE, color: NEUTRALS.N700 },
		h2: { ...TYPERAMP.B2_HEADING, color: NEUTRALS.N700 },
		h3: { ...TYPERAMP.B3_SUBHEAD, color: NEUTRALS.N700 },
		body1: { ...TYPERAMP.B4_BODY, color: NEUTRALS.N700 },
		caption: { ...TYPERAMP.B5_CAPTION, color: NEUTRALS.N600 },
		body2: { ...TYPERAMP.B6_MICRO, color: NEUTRALS.N600 },
		subtitle1: { ...TYPERAMP.B6_CAPTION_BOLD, color: NEUTRALS.N600 },
	},
	deprecatedTheme: weVideoTheme,
	bento: bentoTheme,
	overrides: {
		MuiPaper: {
			root: {
				backgroundColor: colors.surface.mainBg,
				"&.MuiPaper-search": {
					display: "flex",
					boxShadow: "none",
					border: `1px solid ${colors.divider}`,
					borderRadius: 3,
					height: 32,
					width: 270,
				},
			},
		},
		MuiInputBase: {
			root: {
				width: "100%",
				"&.MuiInputBase-search": {
					...TYPERAMP.B4_BODY,
				},
			},
		},
		MuiButton: {
			root: {
				...TYPERAMP.B4_BODY,
				paddingTop: 4,
				paddingBottom: 4,
				paddingLeft: 16,
				paddingRight: 16,
				height: 32,
				minWidth: 88,
				borderRadius: 16,
				textTransform: "initial",
				"&.Mui-disabled": {
					cursor: "not-allowed",
					pointerEvents: "all",
				},
			},
			contained: {
				boxShadow: "",
				"&:hover": {
					boxShadow: "",
				},
				"&:active": {
					boxShadow: "",
				},
				"&.Mui-tertiary": {
					backgroundColor: colors.button.tertiary.container?.rest,
					color: colors.button.tertiary.text.rest,
					"&:hover": {
						backgroundColor: colors.button.tertiary.container?.hover,
					},
					"&:active": {
						backgroundColor: colors.button.tertiary.container?.hover,
					},
					"&.Mui-disabled": {
						backgroundColor: colors.button.tertiary.container?.disabled,
						color: colors.button.tertiary.text.disabled,
					},
				},
			},
			containedPrimary: {
				"&.Mui-disabled": {
					backgroundColor: colors.button.primary.container?.disabled,
					color: colors.button.primary.text.disabled,
				},
				"&:hover": {
					backgroundColor: colors.essentials.brand.shade200,
					"&.Mui-disabled": {
						backgroundColor: colors.button.primary.container?.disabled,
						color: colors.button.primary.text.disabled,
					},
				},
			},
			containedSecondary: {
				backgroundColor: colors.button.secondary.container?.rest,
				color: colors.button.secondary.text.rest,
				"&:hover": {
					backgroundColor: colors.button.secondary.container?.hover,
					color: colors.button.secondary.text.hover,
					"&.Mui-disabled": {
						backgroundColor: colors.button.secondary.container?.disabled,
						color: colors.button.secondary.text.disabled,
					},
				},
				"&:active": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.Mui-accent": {
					backgroundColor: colors.button.secondary.container?.rest,
					color: colors.button.secondary.text.rest,
					"&:hover": {
						backgroundColor: colors.button.secondary.container?.hover,
					},
					"&:active": {
						backgroundColor: colors.button.secondary.container?.hover,
					},
					"&.Mui-disabled": {
						backgroundColor: colors.button.secondary.container?.disabled,
						color: colors.button.secondary.text.disabled,
					},
				},
				"&.Mui-disabled": {
					backgroundColor: colors.button.secondary.container?.disabled,
					color: colors.button.secondary.text.disabled,
				},
			},
			text: {
				padding: 4,
				minWidth: 0,
				"&.Mui-tertiary": {
					color: colors.button.textButton.tertiary.rest,
					backgroundColor: "unset",
					"&:hover": {
						color: colors.button.textButton.tertiary.rest,
						backgroundColor: "unset",
						textDecoration: "underline",
					},
					"&.Mui-disabled": {
						color: colors.button.textButton.tertiary.disabled,
					},
				},
				"&.Mui-primary": {
					color: colors.button.textButton.primary.rest,
					backgroundColor: "unset",
					"&:hover": {
						color: colors.button.textButton.primary.rest,
						backgroundColor: "unset",
					},
					"&.Mui-disabled": {
						color: colors.button.textButton.primary.disabled,
					},
				},
			},
			textPrimary: {
				"&:hover": {
					backgroundColor: "unset",
				},
				"&.Mui-disabled": {
					color: colors.button.primary.container?.disabled,
				},
			},
		},
		MuiIconButton: {
			root: {
				color: bentoTheme.colors.Icon.primary,
				"&:hover": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&:active": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.MuiIconButton-open": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.MuiIconButton-close": {
					"&:hover": {
						backgroundColor: colors.button.secondary.container?.hover,
					},
				},
				"& .MuiIconButton-Text": {
					color: colors.text.primary,
					fontSize: 16,
				},
				"&.SelectIconButton": {
					borderRadius: 2,
					padding: 2,
					width: 20,
					height: 20,
				},
			},
		},
		MuiMenu: {
			paper: {
				backgroundColor: colors.surface.one,
				borderRadius: 4,
				boxShadow: "0 1 6 rgba(0, 0, 0, 0.06)",
			},
			list: {
				backgroundColor: colors.surface.one,
				paddingTop: 5,
				paddingBottom: 5,
			},
		},
		MuiMenuItem: {
			root: {
				minWidth: 112,
				"&:hover": {
					// backgroundColor: colors.BG_HOVER,
				},
				"&:active": {
					// backgroundColor: colors.BG_HOVER,
				},
				"&.MuiMenuItem-parent": {
					paddingRight: 8,
				},
			},
		},
		MuiBreadcrumbs: {
			root: {
				...TYPERAMP.B5_CAPTION,
				"& $ol": {},
			},
			li: {
				fontSize: "13px !important",
				color: `${colors.text.secondary} !important`,
				"& div": {
					"& a": {
						color: "inherit",
					},
				},
			},
		},

		MuiSvgIcon: {
			root: {
				fontSize: "16px",
				lineHeight: "16px",
				"&.MuiIcon-small": {
					fontSize: "8px",
					lineHeight: "8px",
				},
				"&.MuiIcon-large": {
					fontSize: "24px",
					lineHeight: "24px",
				},
				"&.MuiIcon-x-large": {
					fontSize: "40px",
					lineHeight: "40px",
				},
			},
		},
		MuiFormLabel: {
			root: {
				paddingBottom: 4,
			},
		},
		MuiCssBaseline: {
			"@global": {
				".sr-only": {
					border: "0 !important",
					clip: "rect(1px, 1px, 1px, 1px) !important",
					width: "1px !important",
					height: "1px !important",
					margin: "-1px !important",
					padding: "0 !important",
					overflow: "hidden !important",
					clipPath: "inset(50%) !important",
					position: "absolute",
					whiteSpace: "nowrap",
				},
			},
		},
	},
	props: {
		MuiButtonBase: {
			disableRipple: true,
		},
		MuiMenu: {
			// In order to make the screen reader correctly read the number of items in the menu we need to have the menu initialized.
			// By setting the transitionDuration to 0 we make sure the menu is initialized immediately.
			transitionDuration: 0,
		},
	},
});
